import React from 'react';
import EVSEChecklist from './EVSEChecklist';
import EVSEForm from './EVSEForm';
import { CONNECTOR_REQUIRED_FIELDS, CONNECTOR_FIELD_GROUPS } from '../config/evseFields';

const IconPlus = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconTrash = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

// Each connector entry looks like:
// { tempId, chargerTempId, selectedFields: [], formData: {}, errors: {} }

export default function WizardStepConnectors({ chargers, connectors, setConnectors }) {

  const addConnector = () => {
    setConnectors(prev => [
      ...prev,
      {
        tempId: Date.now(),
        chargerTempId: chargers.length > 0 ? chargers[0].tempId : null,
        selectedFields: [],
        formData: {},
        errors: {}
      }
    ]);
  };

  const removeConnector = (tempId) => {
    setConnectors(prev => prev.filter(c => c.tempId !== tempId));
  };

  const updateConnector = (tempId, updates) => {
    setConnectors(prev => prev.map(c => c.tempId === tempId ? { ...c, ...updates } : c));
  };

  const toggleField = (tempId, name) => {
    const connector = connectors.find(c => c.tempId === tempId);
    const isSelected = connector.selectedFields.includes(name);
    const newSelectedFields = isSelected
      ? connector.selectedFields.filter(f => f !== name)
      : [...connector.selectedFields, name];

    const newFormData = { ...connector.formData };
    if (isSelected) delete newFormData[name];

    updateConnector(tempId, { selectedFields: newSelectedFields, formData: newFormData });
  };

  const selectAllInGroup = (tempId, names) => {
    const connector = connectors.find(c => c.tempId === tempId);
    updateConnector(tempId, {
      selectedFields: Array.from(new Set([...connector.selectedFields, ...names]))
    });
  };

  const clearAllInGroup = (tempId, names) => {
    const connector = connectors.find(c => c.tempId === tempId);
    const newFormData = { ...connector.formData };
    names.forEach(n => delete newFormData[n]);
    updateConnector(tempId, {
      selectedFields: connector.selectedFields.filter(f => !names.includes(f)),
      formData: newFormData
    });
  };

  const handleFieldChange = (tempId, name, value) => {
    const connector = connectors.find(c => c.tempId === tempId);
    const newErrors = { ...connector.errors };
    if (newErrors[name]) delete newErrors[name];
    updateConnector(tempId, {
      formData: { ...connector.formData, [name]: value },
      errors: newErrors
    });
  };

  const handleChargerSelect = (tempId, chargerTempId) => {
    updateConnector(tempId, { chargerTempId: Number(chargerTempId) });
  };

  return (
    <>
      {chargers.length === 0 && (
        <div className="card empty-state" style={{ marginBottom: 20 }}>
          <h3>No chargers available</h3>
          <p>Go back to the previous step and add at least one charger before adding connectors.</p>
        </div>
      )}

      {connectors.length === 0 && chargers.length > 0 && (
        <div className="card empty-state" style={{ marginBottom: 20 }}>
          <h3>No connectors added yet</h3>
          <p>Add at least one connector to continue.</p>
        </div>
      )}

      {connectors.map((connector, index) => {
        const linkedCharger = chargers.find(c => c.tempId === connector.chargerTempId);
        const chargerIndex = chargers.findIndex(c => c.tempId === connector.chargerTempId);

        return (
          <div className="entity-card" key={connector.tempId}>
            <div className="entity-card-header">
              <span className="entity-card-title">
                Connector {index + 1}
                {connector.formData.connector_type && (
                  <span className="entity-card-badge">{connector.formData.connector_type}</span>
                )}
              </span>
              <button
                type="button"
                className="entity-remove-btn"
                onClick={() => removeConnector(connector.tempId)}
                title="Remove this connector"
              >
                <IconTrash />
              </button>
            </div>

            {/* ── This Connector Belongs To — references Step 2's chargers ── */}
            <div className="form-group full" style={{ marginBottom: 18, maxWidth: 340 }}>
              <label>This connector belongs to <span className="required">*</span></label>
              <select
                value={connector.chargerTempId || ''}
                onChange={(e) => handleChargerSelect(connector.tempId, e.target.value)}
              >
                {chargers.map((charger, i) => (
                  <option key={charger.tempId} value={charger.tempId}>
                    Charger {i + 1}{charger.formData.ocpp_id ? ` — ${charger.formData.ocpp_id}` : ' (no EVSE ID yet)'}
                  </option>
                ))}
              </select>
            </div>

            <div className="evse-builder">
              <EVSEChecklist
                groups={CONNECTOR_FIELD_GROUPS}
                selectedFields={connector.selectedFields}
                onToggle={(name) => toggleField(connector.tempId, name)}
                onSelectAllInGroup={(names) => selectAllInGroup(connector.tempId, names)}
                onClearAllInGroup={(names) => clearAllInGroup(connector.tempId, names)}
              />
              <EVSEForm
                title={`Connector ${index + 1} Details`}
                requiredFields={CONNECTOR_REQUIRED_FIELDS}
                groups={CONNECTOR_FIELD_GROUPS}
                selectedFields={connector.selectedFields}
                formData={connector.formData}
                onChange={(name, value) => handleFieldChange(connector.tempId, name, value)}
                errors={connector.errors}
                emptyHint="Tick a box on the left to add more details about this connector."
              />
            </div>
          </div>
        );
      })}

      {chargers.length > 0 && (
        <button type="button" className="add-entity-btn" onClick={addConnector}>
          <IconPlus /> Add Connector
        </button>
      )}
    </>
  );
}